import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateApiData } from '../../utils/utils';
import './PaypalButtonWrapper.css';

// Provider-specific payment creation, pricing, approval and capture live in the API.
const PaypalButtonWrapper = ({ subscriptionData, autoStart = false, onAutoStartError }) => {
  const location = useLocation();
  const projectInfo = useSelector(state => state?.projectInfo?.value);
  const accessToken = useSelector(state => state?.accessToken?.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const autoStartedSubscriptionRef = useRef(null);
  const start = useCallback(async () => {
    if (busy || !subscriptionData?.subscription_id) return;
    setBusy(true); setError('');
    const planPrice = subscriptionData?.price ?? subscriptionData?.db_price ?? subscriptionData?.subscription_text;
    const coupon = localStorage.getItem('couponId') || '';
    const previous = (subscriptionData?.isUpgrade || location?.state?.type === 'upgrade') ? localStorage.getItem('previousSubId') || '' : '';
    const purchase = `${projectInfo?.projectConfig?.pubid}:${localStorage.getItem('userId')}:${subscriptionData.subscription_id}:${coupon}:${previous}`;
    const storageKey = `paypal-checkout:${purchase}`;
    let key = sessionStorage.getItem(storageKey);
    if (!key) { key = window.crypto.randomUUID(); sessionStorage.setItem(storageKey, key); }
    try {
      // The API determines the authoritative amount from the subscription ID.
      // Checkout3 can provide a formatted display price (for example "$18.99/mo"),
      // which should not prevent opening a PayPal checkout session.
      const response = await updateApiData({projectDetails:projectInfo, accessToken}, 'paypal/checkout-sessions', {
        path:'common', values:{subscription_id:subscriptionData.subscription_id, coupon_id:coupon || undefined,
          previous_subscription_id:previous || undefined, return_target:localStorage.getItem("isAndroid") === "true" ? "android" : localStorage.getItem("iosSignup") === "true" ? "ios" : "web", idempotency_key:key}
      });
      const checkout = response?.data?.data;
      if (!checkout?.redirectUrl) {
        if (response?.status === 409) sessionStorage.removeItem(storageKey);
        throw new Error(response?.data?.message || response?.message || 'Unable to start PayPal checkout.');
      }
      localStorage.setItem('selectedSubId', String(subscriptionData.subscription_id));
      if (planPrice != null && String(planPrice).trim() !== '') {
        localStorage.setItem('selectedAmount', String(planPrice));
      }
      localStorage.setItem('deviceType', localStorage.getItem('isAndroid') === 'true' ? 'android-web' : 'web');
      sessionStorage.setItem(`paypal-session:${checkout.checkoutSessionId}`, storageKey);
      window.location.assign(checkout.redirectUrl);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Unable to start PayPal checkout. Please retry.');
      if (e?.response?.status === 409) sessionStorage.removeItem(storageKey);
      setBusy(false);
      if (autoStart) onAutoStartError?.();
    }
  }, [accessToken, autoStart, busy, location?.state?.type, onAutoStartError, projectInfo, subscriptionData]);

  useEffect(() => {
    const subscriptionId = subscriptionData?.subscription_id;
    if (!autoStart || !subscriptionId || autoStartedSubscriptionRef.current === subscriptionId) return;

    autoStartedSubscriptionRef.current = subscriptionId;
    start();
  }, [autoStart, start, subscriptionData?.subscription_id]);

  if (autoStart && !error) return null;

  return <div className="paypal-redirect-checkout">
    <button className="paypal-checkout-button" type="button" aria-label={busy ? 'Opening PayPal' : 'Continue with PayPal'} aria-busy={busy} disabled={busy || !subscriptionData?.subscription_id} onClick={start}>
      {busy ? 'Opening PayPal…' : <><span className="paypal-button-wordmark" aria-hidden="true">PayPal</span><span>{[3,8].includes(Number(subscriptionData?.subscription_type_id ?? subscriptionData?.subscription_type)) ? 'Subscribe' : 'Pay Now'}</span></>}
    </button>
    <p className="paypal-powered-by">Powered by <span>PayPal</span></p>
    {error && <p role="alert">{error}</p>}
  </div>;
};
export default PaypalButtonWrapper;
